<script setup>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/api/api';
import { useToast } from 'vue-toastification';
import { useProjectStore } from '@/state/pinia/project';
import { useAuthStore } from '@/state/pinia/auth';
import logoSvg from '@/assets/images/logos/logo.svg';

// SHA-256 helper for gravatar
async function sha256Hex(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

const { t } = useI18n();
const toast = useToast();
const projectStore = useProjectStore();
const authStore = useAuthStore();

// User avatar URL
const userAvatarUrl = ref(null);

// Widget state
const isOpen = ref(false);
const isExpanded = ref(false);
const inputRef = ref(null);

// Project selection
const selectedProjectCode = ref(null);
const showProjectSelector = ref(false);

// Chat state
const conversations = ref([]);
const currentConversation = ref(null);
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const isSending = ref(false);
const messagesContainer = ref(null);

// Agent/Plan state
const pendingPlan = ref(null);
const isExecuting = ref(false);

// Sidebar
const showSidebar = ref(false);

// Computed
const isLoggedIn = computed(() => !!authStore.user);
const projects = computed(() => projectStore.all || []);
const hasProjects = computed(() => projects.value.length > 0);

const currentProjectCode = computed(() => {
  return selectedProjectCode.value || projectStore.activeProject;
});

const currentProjectName = computed(() => {
  if (!currentProjectCode.value) return null;
  const project = projects.value.find(p => p.code === currentProjectCode.value);
  return project?.name || currentProjectCode.value;
});

const needsProjectSelection = computed(() => {
  return !currentProjectCode.value && hasProjects.value;
});

// Watch for project changes - reset conversation
watch(currentProjectCode, () => {
  currentConversation.value = null;
  messages.value = [];
  pendingPlan.value = null;
  conversations.value = [];
  if (currentProjectCode.value && isExpanded.value) {
    fetchConversations();
  }
});

// Handle external open-ai-chat event
const handleOpenAiChat = async (event) => {
  const { projectCode, conversationUuid, prefillMessage } = event.detail || {};

  if (projectCode) {
    selectedProjectCode.value = projectCode;
  }

  // Open the widget
  isOpen.value = true;
  isExpanded.value = true;

  // Wait for project to be set
  await nextTick();

  // If conversation UUID provided, load it
  if (conversationUuid && currentProjectCode.value) {
    await fetchConversations();
    const conv = conversations.value.find(c => c.uuid === conversationUuid);
    if (conv) {
      await loadConversation(conv.uuid);
    }
  }

  // If prefill message provided, set it in the input
  if (prefillMessage) {
    // Start new conversation (clear current)
    currentConversation.value = null;
    messages.value = [];
    pendingPlan.value = null;

    // Set the message
    newMessage.value = prefillMessage;

    // Focus the input
    await nextTick();
    if (inputRef.value) {
      inputRef.value.focus();
    }
  }
};

// Load projects when widget opens and get user avatar
onMounted(async () => {
  if (isLoggedIn.value) {
    if (projects.value.length === 0) {
      projectStore.fetchProjects();
    }
    // Load user avatar
    await updateUserAvatar();
  }

  // Listen for external open-ai-chat events
  window.addEventListener('open-ai-chat', handleOpenAiChat);
});

onBeforeUnmount(() => {
  window.removeEventListener('open-ai-chat', handleOpenAiChat);
});

// Update user avatar from gravatar
const updateUserAvatar = async () => {
  const email = (authStore.user?.email || '').trim().toLowerCase();
  if (!email) {
    userAvatarUrl.value = null;
    return;
  }

  const cacheKey = `gravatar-sha256-${email}`;
  let hash = localStorage.getItem(cacheKey);
  if (!hash) {
    hash = await sha256Hex(email);
    localStorage.setItem(cacheKey, hash);
  }

  userAvatarUrl.value = `https://gravatar.com/avatar/${hash}?size=40&d=initials`;
};

// Toggle widget open/close
const toggleWidget = () => {
  if (!isLoggedIn.value) return;

  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    if (projects.value.length === 0) {
      projectStore.fetchProjects();
    }
    // Fetch conversations when opening widget
    if (currentProjectCode.value) {
      fetchConversations();
    }
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
};

// Select project
const selectProject = (code) => {
  selectedProjectCode.value = code;
  showProjectSelector.value = false;
  nextTick(() => {
    inputRef.value?.focus();
  });
};

// Change project (show selector)
const changeProject = () => {
  showProjectSelector.value = true;
};

// Collapse back to mini view
const collapseChat = () => {
  isExpanded.value = false;
  showSidebar.value = false;
};

// Toggle sidebar (history)
const toggleSidebar = async () => {
  showSidebar.value = !showSidebar.value;
  if (showSidebar.value && conversations.value.length === 0) {
    await fetchConversations();
  }
};

// Fetch conversations
const fetchConversations = async () => {
  if (!currentProjectCode.value) return;
  try {
    const { data } = await api.get(`/api/projects/${currentProjectCode.value}/ai-chat`);
    conversations.value = data.data;
  } catch (error) {
    console.error('Failed to fetch conversations:', error);
  }
};

// Create new conversation
const createConversation = async () => {
  if (!currentProjectCode.value) return;
  try {
    const { data } = await api.post(`/api/projects/${currentProjectCode.value}/ai-chat`);
    currentConversation.value = data.data;
    messages.value = [];
    pendingPlan.value = null;
    await fetchConversations();
  } catch (error) {
    toast.error(t('ai.chat.errors.createFailed'));
  }
};

// Load conversation
const loadConversation = async (uuid) => {
  if (!currentProjectCode.value) return;
  isLoading.value = true;
  pendingPlan.value = null;
  try {
    const { data } = await api.get(`/api/projects/${currentProjectCode.value}/ai-chat/${uuid}`);
    currentConversation.value = data.data;
    messages.value = data.data.messages || [];
    showSidebar.value = false;
    await nextTick();
    scrollToBottom();
  } catch (error) {
    toast.error(t('ai.chat.errors.loadFailed'));
  } finally {
    isLoading.value = false;
  }
};

// Send message
const sendMessage = async () => {
  if (!newMessage.value.trim() || isSending.value) return;

  // Check if project is selected
  if (!currentProjectCode.value) {
    showProjectSelector.value = true;
    return;
  }

  // Auto-expand on first message
  if (!isExpanded.value) {
    isExpanded.value = true;
  }

  if (!currentConversation.value) {
    await createConversation();
  }

  const userMessage = newMessage.value.trim();
  newMessage.value = '';

  // Add user message to UI
  messages.value.push({
    uuid: 'temp-' + Date.now(),
    role: 'user',
    content: userMessage,
    created_at: new Date().toISOString(),
  });

  await nextTick();
  scrollToBottom();

  isSending.value = true;

  try {
    const { data } = await api.post(
      `/api/projects/${currentProjectCode.value}/ai-chat/${currentConversation.value.uuid}/plan`,
      { message: userMessage },
      { timeout: 600000 } // 10 minutes for AI requests
    );

    if (data.actions && data.actions.length > 0) {
      pendingPlan.value = {
        message: data.message,
        actions: data.actions,
        tokens_used: data.tokens_used,
      };
    } else {
      messages.value.push({
        uuid: 'resp-' + Date.now(),
        role: 'assistant',
        content: data.message,
        tokens_used: data.tokens_used,
        created_at: new Date().toISOString(),
      });

      if (data.conversation_title) {
        currentConversation.value.title = data.conversation_title;
        await fetchConversations();
      }
    }

    await nextTick();
    scrollToBottom();
  } catch (error) {
    if (error.response?.status === 402) {
      const available = error.response?.data?.available_tokens || 0;
      toast.error(`Brak tokenów AI (dostępne: ${available}). Dokup tokeny w ustawieniach workspace.`);
    } else {
      toast.error(t('ai.chat.errors.sendFailed'));
    }
    messages.value.pop();
  } finally {
    isSending.value = false;
  }
};

// Approve plan
const approvePlan = async () => {
  console.log('approvePlan called', { pendingPlan: pendingPlan.value, currentProjectCode: currentProjectCode.value });
  if (!pendingPlan.value || !currentProjectCode.value) {
    console.log('approvePlan: early return', { pendingPlan: !!pendingPlan.value, currentProjectCode: !!currentProjectCode.value });
    return;
  }

  console.log('approvePlan: executing', { actions: pendingPlan.value.actions });
  isExecuting.value = true;

  try {
    const { data } = await api.post(
      `/api/projects/${currentProjectCode.value}/ai-chat/${currentConversation.value.uuid}/execute`,
      {
        message: pendingPlan.value.message,
        actions: pendingPlan.value.actions
      },
      { timeout: 600000 } // 10 minutes for AI requests
    );

    messages.value.push({
      uuid: 'resp-' + Date.now(),
      role: 'assistant',
      content: pendingPlan.value.message,
      actions: data.actions,
      tokens_used: pendingPlan.value.tokens_used,
      created_at: new Date().toISOString(),
    });

    // Refresh project data if actions were executed
    if (data.actions && data.actions.length > 0) {
      projectStore.fetchAllForActive();
    }

    pendingPlan.value = null;
    await fetchConversations();
    await nextTick();
    scrollToBottom();
  } catch (error) {
    toast.error(t('ai.chat.errors.executeFailed'));
  } finally {
    isExecuting.value = false;
  }
};

// Reject plan
const rejectPlan = () => {
  pendingPlan.value = null;
  messages.value.pop();
};

// Delete conversation
const deleteConversation = async (uuid) => {
  if (!currentProjectCode.value) return;
  try {
    await api.delete(`/api/projects/${currentProjectCode.value}/ai-chat/${uuid}`);
    if (currentConversation.value?.uuid === uuid) {
      currentConversation.value = null;
      messages.value = [];
      pendingPlan.value = null;
    }
    await fetchConversations();
    toast.success(t('ai.chat.toast.deleted'));
  } catch (error) {
    toast.error(t('ai.chat.errors.deleteFailed'));
  }
};

// Scroll to bottom
const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// Handle Enter key
const handleKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};

// Format message with markdown-like syntax
const formatMessage = (content) => {
  if (!content) return '';

  // Process line by line for headers
  const lines = content.split('\n');
  const processedLines = lines.map(line => {
    // Headers: ## Header or ### Header
    if (line.startsWith('### ')) {
      return `<h4 class="ai-heading">${line.slice(4)}</h4>`;
    }
    if (line.startsWith('## ')) {
      return `<h3 class="ai-heading">${line.slice(3)}</h3>`;
    }
    if (line.startsWith('# ')) {
      return `<h2 class="ai-heading">${line.slice(2)}</h2>`;
    }
    // Bullet lists: - item or * item
    if (line.match(/^[-*]\s+/)) {
      return `<li>${line.slice(2)}</li>`;
    }
    // Numbered lists: 1. item
    if (line.match(/^\d+\.\s+/)) {
      return `<li>${line.replace(/^\d+\.\s+/, '')}</li>`;
    }
    // Horizontal rule: ---
    if (line.match(/^-{3,}$/)) {
      return '<hr class="ai-hr">';
    }
    return line;
  });

  // Join and wrap consecutive <li> in <ul>
  let html = processedLines.join('\n');
  html = html.replace(/(<li>.*?<\/li>\n?)+/gs, match => `<ul class="ai-list">${match}</ul>`);

  // Inline formatting
  html = html
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>');

  return html;
};

// Get action icon
const getActionIcon = (type) => {
  const icons = {
    'UPDATE_STYLES': 'bx-palette',
    'CREATE_PAGE': 'bx-file-blank',
    'UPDATE_PAGE': 'bx-edit',
    'DELETE_PAGE': 'bx-trash',
    'CREATE_COLLECTION': 'bx-collection',
    'ADD_COLLECTION_FIELD': 'bx-plus-circle',
    'DELETE_COLLECTION': 'bx-trash',
    'CREATE_MENU': 'bx-menu',
    'UPDATE_MENU': 'bx-edit',
  };
  return icons[type] || 'bx-cog';
};

// Get action label
const getActionLabel = (action) => {
  const labels = {
    'UPDATE_STYLES': 'Zmień style',
    'CREATE_PAGE': `Utwórz stronę "${action.params?.name || ''}"`,
    'UPDATE_PAGE': `Edytuj stronę "${action.params?.code || ''}"`,
    'DELETE_PAGE': `Usuń stronę "${action.params?.code || ''}"`,
    'CREATE_COLLECTION': `Utwórz kolekcję "${action.params?.name || ''}"`,
    'ADD_COLLECTION_FIELD': `Dodaj pole "${action.params?.name || ''}"`,
    'DELETE_COLLECTION': `Usuń kolekcję "${action.params?.code || ''}"`,
    'CREATE_MENU': `Utwórz menu "${action.params?.name || ''}"`,
    'UPDATE_MENU': `Edytuj menu "${action.params?.code || ''}"`,
  };
  return labels[action.type] || action.type;
};

// Start new conversation
const startNewConversation = () => {
  currentConversation.value = null;
  messages.value = [];
  pendingPlan.value = null;
  showSidebar.value = false;
};

// Has messages
const hasMessages = computed(() => messages.value.length > 0 || pendingPlan.value);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isLoggedIn"
      class="ai-widget"
      :class="{ 'is-open': isOpen, 'is-expanded': isExpanded }"
    >
      <!-- Floating Button (Chmurka) -->
      <button
        v-if="!isOpen"
        class="ai-widget__bubble"
        @click="toggleWidget"
      >
        <img
          :src="logoSvg"
          alt="AI"
          class="ai-widget__bubble-logo"
        >
      </button>

      <!-- Chat Container -->
      <div
        v-if="isOpen"
        class="ai-widget__container"
      >
        <!-- Header -->
        <div class="ai-widget__header">
          <div class="ai-widget__header-left">
            <button
              v-if="currentProjectCode"
              class="ai-widget__header-btn"
              :title="$t('ai.chat.history')"
              @click="toggleSidebar"
            >
              <i class="bx bx-history" />
            </button>
            <div class="ai-widget__header-logo">
              <img
                :src="logoSvg"
                alt="AI"
              >
            </div>
            <span class="ai-widget__title">{{ $t('ai.chat.title') }}</span>
          </div>
          <div class="ai-widget__header-right">
            <button
              v-if="isExpanded && hasMessages"
              class="ai-widget__header-btn"
              :title="$t('ai.chat.newConversation')"
              @click="startNewConversation"
            >
              <i class="bx bx-plus" />
            </button>
            <button
              v-if="isExpanded"
              class="ai-widget__header-btn"
              :title="$t('common.minimize')"
              @click="collapseChat"
            >
              <i class="bx bx-minus" />
            </button>
            <button
              class="ai-widget__header-btn"
              :title="$t('common.close')"
              @click="isOpen = false; isExpanded = false"
            >
              <i class="bx bx-x" />
            </button>
          </div>
        </div>

        <!-- Project indicator -->
        <div
          v-if="currentProjectCode && !showProjectSelector"
          class="ai-widget__project-bar"
        >
          <span class="ai-widget__project-label">
            <i class="bx bx-folder" />
            {{ currentProjectName }}
          </span>
          <button
            class="ai-widget__project-change"
            @click="changeProject"
          >
            {{ $t('common.change') }}
          </button>
        </div>

        <!-- Project Selector -->
        <div
          v-if="showProjectSelector || needsProjectSelection"
          class="ai-widget__project-selector"
        >
          <div class="ai-widget__project-selector-header">
            <i class="bx bx-folder-open" />
            <span>{{ $t('ai.chat.selectProject') }}</span>
          </div>
          <div class="ai-widget__project-list">
            <button
              v-for="project in projects"
              :key="project.code"
              class="ai-widget__project-item"
              :class="{ 'is-active': project.code === currentProjectCode }"
              @click="selectProject(project.code)"
            >
              {{ project.name }}
            </button>
          </div>
          <button
            v-if="showProjectSelector && currentProjectCode"
            class="ai-widget__project-cancel"
            @click="showProjectSelector = false"
          >
            {{ $t('common.cancel') }}
          </button>
        </div>

        <!-- Sidebar (History) -->
        <transition name="slide-sidebar">
          <div
            v-if="showSidebar && !showProjectSelector"
            class="ai-widget__sidebar"
          >
            <div class="ai-widget__sidebar-header">
              <span>{{ $t('ai.chat.history') }}</span>
              <button
                class="ai-widget__header-btn"
                @click="showSidebar = false"
              >
                <i class="bx bx-x" />
              </button>
            </div>
            <div class="ai-widget__sidebar-list">
              <div
                v-for="conv in conversations"
                :key="conv.uuid"
                class="ai-widget__conv-item"
                :class="{ 'is-active': currentConversation?.uuid === conv.uuid }"
                @click="loadConversation(conv.uuid)"
              >
                <div class="ai-widget__conv-title">
                  {{ conv.title || $t('ai.chat.untitled') }}
                </div>
                <div class="ai-widget__conv-meta">
                  {{ conv.messages_count }} {{ $t('ai.chat.messages') }}
                </div>
                <button
                  class="ai-widget__conv-delete"
                  @click.stop="deleteConversation(conv.uuid)"
                >
                  <i class="bx bx-trash" />
                </button>
              </div>
              <div
                v-if="conversations.length === 0"
                class="ai-widget__sidebar-empty"
              >
                {{ $t('ai.chat.noConversations') }}
              </div>
            </div>
          </div>
        </transition>

        <!-- Body -->
        <div
          v-if="!showProjectSelector && !needsProjectSelection"
          class="ai-widget__body"
        >
          <!-- Welcome / Compact View -->
          <div
            v-if="!isExpanded && !hasMessages"
            class="ai-widget__welcome-mini"
          >
            <p>{{ $t('ai.chat.welcome.mini') }}</p>
          </div>

          <!-- Messages (Expanded) -->
          <div
            v-if="isExpanded || hasMessages"
            ref="messagesContainer"
            class="ai-widget__messages"
          >
            <div
              v-if="isLoading"
              class="ai-widget__loading"
            >
              <div class="spinner-border spinner-border-sm" />
            </div>

            <template v-if="!isLoading">
              <!-- Welcome if no messages -->
              <div
                v-if="!hasMessages && isExpanded"
                class="ai-widget__welcome"
              >
                <div class="ai-widget__welcome-logo">
                  <img
                    :src="logoSvg"
                    alt="LessCMS AI"
                  >
                </div>
                <h5>{{ $t('ai.chat.welcome.title') }}</h5>
                <p>{{ $t('ai.chat.welcome.description') }}</p>
                <div class="ai-widget__examples">
                  <button
                    v-for="example in ['Zmień kolor główny na niebieski', 'Stwórz stronę O nas', 'Dodaj kolekcję blog']"
                    :key="example"
                    class="ai-widget__example"
                    @click="newMessage = example; inputRef?.focus()"
                  >
                    "{{ example }}"
                  </button>
                </div>
              </div>

              <!-- Messages -->
              <div
                v-for="msg in messages"
                :key="msg.uuid"
                class="ai-widget__message"
                :class="`ai-widget__message--${msg.role}`"
              >
                <div class="ai-widget__message-avatar">
                  <div
                    v-if="msg.role === 'assistant'"
                    class="ai-widget__avatar-ai"
                  >
                    <img
                      :src="logoSvg"
                      alt="AI"
                    >
                  </div>
                  <img
                    v-else-if="userAvatarUrl"
                    :src="userAvatarUrl"
                    alt="User"
                    class="ai-widget__avatar-user"
                  >
                  <i
                    v-else
                    class="bx bx-user"
                  />
                </div>
                <div class="ai-widget__message-content">
                  <div
                    class="ai-widget__message-text"
                    v-html="formatMessage(msg.content)"
                  />
                  <div
                    v-if="msg.actions?.length"
                    class="ai-widget__message-actions"
                  >
                    <div
                      v-for="(action, i) in msg.actions"
                      :key="i"
                      class="ai-widget__action-badge"
                      :class="action.success ? 'is-success' : 'is-error'"
                    >
                      <i :class="action.success ? 'bx bx-check' : 'bx bx-x'" />
                      {{ action.type }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Thinking -->
              <div
                v-if="isSending"
                class="ai-widget__message ai-widget__message--assistant"
              >
                <div class="ai-widget__message-avatar">
                  <div class="ai-widget__avatar-ai">
                    <img
                      :src="logoSvg"
                      alt="AI"
                    >
                  </div>
                </div>
                <div class="ai-widget__message-content">
                  <div class="ai-widget__thinking">
                    <i class="bx bx-loader-alt bx-spin" />
                    {{ $t('ai.chat.plan.thinking') }}
                  </div>
                </div>
              </div>

              <!-- Pending Plan -->
              <div
                v-if="pendingPlan"
                class="ai-widget__plan"
              >
                <div class="ai-widget__plan-header">
                  <div class="ai-widget__avatar-ai ai-widget__avatar-ai--small">
                    <img
                      :src="logoSvg"
                      alt="AI"
                    >
                  </div>
                  <span>{{ $t('ai.chat.plan.title') }}</span>
                </div>
                <div class="ai-widget__plan-message">
                  {{ pendingPlan.message }}
                </div>
                <div class="ai-widget__plan-actions">
                  <div
                    v-for="(action, i) in pendingPlan.actions"
                    :key="i"
                    class="ai-widget__plan-action"
                  >
                    <i :class="`bx ${getActionIcon(action.type)}`" />
                    <span>{{ getActionLabel(action) }}</span>
                  </div>
                </div>
                <div class="ai-widget__plan-buttons">
                  <button
                    class="btn btn-sm btn-success"
                    :disabled="isExecuting"
                    @click="approvePlan"
                  >
                    <i :class="isExecuting ? 'bx bx-loader-alt bx-spin' : 'bx bx-check'" />
                    {{ isExecuting ? $t('ai.chat.plan.executing') : $t('ai.chat.plan.approve') }}
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="isExecuting"
                    @click="rejectPlan"
                  >
                    {{ $t('ai.chat.plan.reject') }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Input -->
        <div
          v-if="!showProjectSelector && !needsProjectSelection"
          class="ai-widget__input"
        >
          <input
            ref="inputRef"
            v-model="newMessage"
            type="text"
            :placeholder="$t('ai.chat.placeholder')"
            :disabled="isSending || isExecuting || !!pendingPlan"
            @keydown="handleKeydown"
          >
          <button
            class="ai-widget__send"
            :disabled="!newMessage.trim() || isSending || isExecuting || !!pendingPlan"
            @click="sendMessage"
          >
            <i :class="isSending ? 'bx bx-loader-alt bx-spin' : 'bx bx-send'" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.ai-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

// Floating Bubble
.ai-widget__bubble {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e8e8ff;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 12px;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 28px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
}

.ai-widget__bubble-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

// Container
.ai-widget__container {
  width: 360px;
  max-height: calc(100vh - 100px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;

  .is-expanded & {
    width: 440px;
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
  }
}

// Header
.ai-widget__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #e7f3ff 100%);
  border-bottom: 1px solid #e5e7eb;
}

.ai-widget__header-left,
.ai-widget__header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-widget__title {
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
}

.ai-widget__header-logo {
  width: 24px;
  height: 24px;
  background: #e8e8ff;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ai-widget__header-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1f2937;
  }

  i { font-size: 18px; }
}

// Project Bar
.ai-widget__project-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #f0f9ff;
  border-bottom: 1px solid #e0f2fe;
  font-size: 12px;
}

.ai-widget__project-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0369a1;
  font-weight: 500;

  i { font-size: 14px; }
}

.ai-widget__project-change {
  background: none;
  border: none;
  color: #0ea5e9;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: rgba(14, 165, 233, 0.1);
  }
}

// Project Selector
.ai-widget__project-selector {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.ai-widget__project-selector-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #1f2937;

  i {
    font-size: 20px;
    color: #50a5f1;
  }
}

.ai-widget__project-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-widget__project-item {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #50a5f1;
    background: #f0f9ff;
  }

  &.is-active {
    border-color: #50a5f1;
    background: #e7f3ff;
    font-weight: 500;
  }
}

.ai-widget__project-cancel {
  margin-top: 12px;
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
}

// Sidebar
.ai-widget__sidebar {
  position: absolute;
  top: 52px;
  left: 0;
  bottom: 56px;
  width: 240px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  z-index: 10;
  display: flex;
  flex-direction: column;
}

.ai-widget__sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 13px;
}

.ai-widget__sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.ai-widget__conv-item {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  position: relative;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
    .ai-widget__conv-delete { opacity: 1; }
  }

  &.is-active {
    background: #e7f3ff;
  }
}

.ai-widget__conv-title {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 24px;
}

.ai-widget__conv-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.ai-widget__conv-delete {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #ef4444;
  opacity: 0;
  cursor: pointer;
  padding: 4px;
  transition: opacity 0.2s;
}

.ai-widget__sidebar-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 20px;
}

.slide-sidebar-enter-active,
.slide-sidebar-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-sidebar-enter-from,
.slide-sidebar-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

// Body
.ai-widget__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 120px;

  .is-expanded & {
    min-height: 0;
  }
}

.ai-widget__welcome-mini {
  padding: 16px;
  text-align: center;

  p {
    margin: 0;
    color: #6b7280;
    font-size: 13px;
  }
}

.ai-widget__welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;

  h5 {
    margin: 12px 0 8px;
    font-size: 16px;
    color: #1f2937;
  }

  p {
    margin: 0 0 16px;
    color: #6b7280;
    font-size: 13px;
  }
}

.ai-widget__welcome-logo {
  width: 64px;
  height: 64px;
  background: #e8e8ff;
  border-radius: 16px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ai-widget__examples {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.ai-widget__example {
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    border-color: #50a5f1;
    background: #fff;
  }
}

// Messages
.ai-widget__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.ai-widget__loading {
  text-align: center;
  padding: 20px;
}

.ai-widget__message {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;

  &--user {
    flex-direction: row-reverse;

    .ai-widget__message-content {
      background: #50a5f1;
      color: #fff;
    }

    .ai-widget__message-avatar {
      background: #e5e7eb;
      color: #4b5563;
    }
  }

  &--assistant {
    .ai-widget__message-content {
      background: #f3f4f6;
    }

    .ai-widget__message-avatar {
      background: #e7f3ff;
    }
  }
}

.ai-widget__message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i { font-size: 16px; }
}

.ai-widget__avatar-ai {
  width: 32px;
  height: 32px;
  background: #e8e8ff;
  border-radius: 50%;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &--small {
    width: 24px;
    height: 24px;
    padding: 4px;
  }
}

.ai-widget__avatar-user {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.ai-widget__message-content {
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;

  :deep(code) {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }

  // Markdown styles
  :deep(.ai-heading) {
    margin: 12px 0 8px 0;
    font-weight: 600;
    line-height: 1.3;

    &:first-child {
      margin-top: 0;
    }
  }

  :deep(h2.ai-heading) {
    font-size: 16px;
  }

  :deep(h3.ai-heading) {
    font-size: 14px;
  }

  :deep(h4.ai-heading) {
    font-size: 13px;
  }

  :deep(.ai-list) {
    margin: 8px 0;
    padding-left: 20px;

    li {
      margin: 4px 0;
    }
  }

  :deep(.ai-hr) {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 12px 0;
  }

  :deep(strong) {
    font-weight: 600;
  }

  :deep(em) {
    font-style: italic;
  }
}

.ai-widget__message-actions {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ai-widget__action-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &.is-success {
    background: rgba(34, 197, 94, 0.15);
    color: #16a34a;
  }

  &.is-error {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
  }
}

.ai-widget__thinking {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 13px;
}

// Plan
.ai-widget__plan {
  background: linear-gradient(135deg, #f8fafc 0%, #e7f3ff 100%);
  border: 1px solid #50a5f1;
  border-radius: 12px;
  padding: 12px;
  margin-top: 8px;
}

.ai-widget__plan-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;
  color: #50a5f1;
  margin-bottom: 8px;
}

.ai-widget__plan-message {
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 10px;
  line-height: 1.5;
}

.ai-widget__plan-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.ai-widget__plan-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e5e7eb;

  i {
    font-size: 16px;
    color: #50a5f1;
  }
}

.ai-widget__plan-buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  .btn {
    font-size: 12px;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

// Input
.ai-widget__input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;

  input {
    flex: 1;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    padding: 10px 16px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #50a5f1;
    }

    &:disabled {
      background: #f3f4f6;
    }
  }
}

.ai-widget__send {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #50a5f1;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #3b82f6;
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }

  i { font-size: 18px; }
}

// Mobile
@media (max-width: 480px) {
  .ai-widget__container {
    width: calc(100vw - 32px);

    .is-expanded & {
      width: calc(100vw - 32px);
      height: calc(100vh - 100px);
    }
  }
}
</style>
